// v2/components/admin/RestrictionModal.js — borc kisitlama kurali ekle/guncelle/sil
// Kural eski sistemle AYNI:
//   - min_borc_gun: bu gunden eski FIFO borc varsa kisitlama gecerli
//   - gun_sayisi  : kisitlama aktif olunca musteriye taninan odeme suresi
//   - vadesi gecmis borc <= 100 TL ise kural kaydedilir ama AKTIF EDILMEZ
//   - "Kurali kaldir" kaydi TAMAMEN siler (geri alinamaz)
import React, { useCallback, useEffect, useState } from 'react';
import {
  X, Save, Loader2, AlertTriangle, Trash2, ShieldAlert, Info, CalendarClock,
} from 'lucide-react';
import { adminDebtApi } from '../../hooks/useAdminDebt';
import { formatTRY } from '../../utils/format';
import s from './RestrictionModal.module.css';

const VARSAYILAN_MESAJ =
  'Borç ödemeniz gecikmiştir. Belirtilen süre içinde ödeme yapmazsanız sisteme giriş yapamayacaksınız.';
const VADE_SECENEK = [30, 45, 60, 90, 120, 150];

export default function RestrictionModal({ acik, musteri, onKapat, onKaydedildi }) {
  const kural = musteri && musteri.restriction;
  const varOlan = Boolean(kural);

  const [minGun, setMinGun] = useState(60);
  const [sure, setSure] = useState(5);
  const [mesaj, setMesaj] = useState(VARSAYILAN_MESAJ);
  const [not, setNot] = useState('');
  const [kaydediyor, setKaydediyor] = useState(false);
  const [siliyor, setSiliyor] = useState(false);
  const [silOnay, setSilOnay] = useState(false);
  const [hata, setHata] = useState(null);

  useEffect(() => {
    if (!acik) return;
    setHata(null);
    setSilOnay(false);
    setMinGun((kural && kural.min_borc_gun) || 60);
    setSure((kural && kural.gun_sayisi) || 5);
    setMesaj(VARSAYILAN_MESAJ);
    setNot('');
  }, [acik, kural]);

  useEffect(() => {
    if (!acik) return undefined;
    const esc = (e) => { if (e.key === 'Escape' && !kaydediyor && !siliyor) onKapat(); };
    const onceki = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', esc);
    return () => {
      document.body.style.overflow = onceki;
      document.removeEventListener('keydown', esc);
    };
  }, [acik, kaydediyor, siliyor, onKapat]);

  const kaydet = useCallback(async () => {
    if (kaydediyor) return;
    setHata(null);
    setKaydediyor(true);
    try {
      const v = await adminDebtApi.kisitlamaKaydet({
        musteri_kodu: musteri.code,
        musteri_adi: musteri.title || '',
        min_borc_gun: Number(minGun),
        gun_sayisi: Number(sure),
        uyari_mesaji: mesaj.trim() || VARSAYILAN_MESAJ,
        notlar: not.trim(),
        kisitlama_aktif: true,
      });
      if (!v || !v.success) throw new Error((v && v.message) || 'Kaydedilemedi');
      onKaydedildi(v.message || 'Kısıtlama kuralı kaydedildi', v);
    } catch (e) {
      setHata((e && e.message) || 'Kaydedilemedi');
    } finally {
      setKaydediyor(false);
    }
  }, [kaydediyor, musteri, minGun, sure, mesaj, not, onKaydedildi]);

  const sil = useCallback(async () => {
    if (siliyor) return;
    setHata(null);
    setSiliyor(true);
    try {
      const v = await adminDebtApi.kisitlamaSil(musteri.code);
      if (!v || !v.success) throw new Error((v && v.message) || 'Kaldırılamadı');
      onKaydedildi(v.message || 'Kısıtlama kuralı kaldırıldı', v);
    } catch (e) {
      setHata((e && e.message) || 'Kaldırılamadı');
      setSiliyor(false);
    }
  }, [siliyor, musteri, onKaydedildi]);

  if (!acik || !musteri) return null;

  const vadesiGecmis = Number(musteri.overdue) || 0;
  const esikAltinda = vadesiGecmis <= 100;

  return (
    <div className={s.overlay} role="dialog" aria-modal="true" aria-label="Borç kısıtlama kuralı">
      <div className={s.backdrop} onClick={() => !kaydediyor && !siliyor && onKapat()} aria-hidden="true" />
      <div className={s.modal}>
        <div className={s.head}>
          <span className={s.headIcon}><ShieldAlert size={19} /></span>
          <div className={s.headText}>
            <h3 className={s.title}>{varOlan ? 'Kısıtlama Kuralını Düzenle' : 'Kısıtlama Kuralı Ekle'}</h3>
            <p className={s.sub}>{musteri.title || musteri.code} · <b>{musteri.code}</b></p>
          </div>
          <button type="button" className={s.closeBtn} onClick={onKapat}
            disabled={kaydediyor || siliyor} aria-label="Kapat"><X size={18} /></button>
        </div>

        <div className={s.body}>
          {hata && <div className={s.error}><AlertTriangle size={16} /> <span>{hata}</span></div>}

          <div className={s.info}>
            <div className={s.infoRow}>
              <span>Net bakiye</span><b>{formatTRY(musteri.balance)}</b>
            </div>
            <div className={s.infoRow}>
              <span>Vadesi geçmiş (31+ gün)</span>
              <b className={vadesiGecmis > 0 ? s.debt : ''}>{formatTRY(vadesiGecmis)}</b>
            </div>
            {varOlan && (
              <div className={s.infoRow}>
                <span>Mevcut durum</span>
                <b className={kural.aktif ? s.debt : ''}>
                  {kural.aktif ? 'Kısıtlama aktif' : 'Kural var, kısıtlama pasif'}
                  {kural.aktif && kural.kalan_gun !== null && kural.kalan_gun !== undefined
                    ? ` · ${kural.kalan_gun} gün kaldı` : ''}
                </b>
              </div>
            )}
          </div>

          <label className={s.field}>
            <span className={s.label}>Vade eşiği (gün)</span>
            <div className={s.chips}>
              {VADE_SECENEK.map((g) => (
                <button key={g} type="button"
                  className={`${s.chip} ${Number(minGun) === g ? s.chipOn : ''}`}
                  onClick={() => setMinGun(g)}>{g}</button>
              ))}
              <input className={s.numInput} type="number" min="1" max="365"
                value={minGun} onChange={(e) => setMinGun(e.target.value)} />
            </div>
            <span className={s.hint}>
              Bu günden daha eski ödenmemiş borcu varsa müşteri kısıtlanır.
            </span>
          </label>

          <label className={s.field}>
            <span className={s.label}><CalendarClock size={13} /> Ödeme süresi (gün)</span>
            <input className={s.numInput} type="number" min="1" max="90"
              value={sure} onChange={(e) => setSure(e.target.value)} />
            <span className={s.hint}>
              Kısıtlama aktif olduğunda müşteriye tanınan süre. Süre dolunca giriş engellenir.
            </span>
          </label>

          <label className={s.field}>
            <span className={s.label}>Uyarı mesajı</span>
            <textarea className={s.textarea} rows={3} value={mesaj}
              onChange={(e) => setMesaj(e.target.value)} maxLength={500} />
            <span className={s.hint}>Müşteriye giriş ekranında gösterilir.</span>
          </label>

          <label className={s.field}>
            <span className={s.label}>Not (isteğe bağlı)</span>
            <input className={s.input} value={not} onChange={(e) => setNot(e.target.value)}
              placeholder="Örn: müşteriyle görüşüldü, 2 hafta süre verildi" maxLength={200} />
          </label>

          {esikAltinda && (
            <div className={s.notice}>
              <Info size={16} />
              <span>
                Müşterinin {minGun} günü geçmiş borcu 100 TL'nin altında. Kural kaydedilir
                ama <b>kısıtlama şimdi aktif olmaz</b>; borç eşiği aştığında sistem
                otomatik devreye alır.
              </span>
            </div>
          )}
        </div>

        <div className={s.foot}>
          {varOlan && (
            silOnay ? (
              <div className={s.delConfirm}>
                <span>Kural tamamen silinecek. Emin misiniz?</span>
                <button type="button" className={s.delNo} onClick={() => setSilOnay(false)}
                  disabled={siliyor}>Vazgeç</button>
                <button type="button" className={s.delYes} onClick={sil} disabled={siliyor}>
                  {siliyor ? <Loader2 size={14} className={s.spin} /> : <Trash2 size={14} />} Sil
                </button>
              </div>
            ) : (
              <button type="button" className={s.delBtn} onClick={() => setSilOnay(true)}
                disabled={kaydediyor}>
                <Trash2 size={15} /> Kuralı Kaldır
              </button>
            )
          )}
          {!silOnay && (
            <div className={s.footRight}>
              <button type="button" className={s.cancelBtn} onClick={onKapat}
                disabled={kaydediyor || siliyor}>İptal</button>
              <button type="button" className={s.saveBtn} onClick={kaydet}
                disabled={kaydediyor || siliyor}>
                {kaydediyor ? <Loader2 size={16} className={s.spin} /> : <Save size={16} />}
                {kaydediyor ? 'Kaydediliyor…' : 'Kaydet'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
